import { Component, OnDestroy, OnInit, effect, input, ViewChild } from '@angular/core';
import { Table, TableFilterEvent, TableModule } from 'primeng/table';
import { TableComponent } from '../../../../core/components/table/table.component';
import { Nature } from './nature.model';
import { HttpErrorResponse } from '@angular/common/http';
import { ReferentialsNaturesService } from './referentials-natures.service';
import { ButtonModule } from 'primeng/button';
import { TableReadOnlyComponent } from '../../../../core/components/table-read-only/table-read-only.component';
import { map, of, Subject, Subscription, takeUntil } from 'rxjs';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { FilterMetadata, MessageService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { AddNatureComponent } from './add-nature/add-nature.component';
import { StoreService } from '../../../../core/services/store.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InlineResponse } from '../../../../core/models/inline-response.model';
import { GenericErrorResponse } from '../../../../core/models/generic-response.model';
import { TooltipModule } from 'primeng/tooltip';
import { includes } from 'cypress/types/lodash';
import { TranslocoService, TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { FilterEventService } from "../../../../core/services/filter-event.service";

type NatureFormArray = {
  id: FormControl<Number | null>;
  codeHecate: FormControl<Number | null>;
  designation: FormControl<string | null>;
};

const dialogConfig = {
  modal: true,
  width: '30vw',
  height: 'auto',
  maximizable: true,
  breakpoints: {
    '1199px': '60vw', // Largeur à 60% du viewport si la fenêtre fait moins de 1199px de large
    '991px': '70vw', // Largeur à 70% du viewport si la fenêtre fait moins de 991px
    '575px': '90vw', // Largeur à 90% du viewport si la fenêtre fait moins de 575px
  },
};

@Component({
  selector: 'app-referentials-natures',
  standalone: true,
  imports: [CommonModule, TableModule, ButtonModule, InputTextModule, ReactiveFormsModule, FormsModule, ToastModule, TooltipModule, TranslocoModule],
  providers: [DialogService, MessageService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './referentials-natures.component.html',
  styleUrl: './referentials-natures.component.css',
})
export class ReferentialsNaturesComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private filterSubscription!: Subscription;
  @ViewChild('natureTable') table!: Table;

  userHasReadWritePermission: boolean = false;

  selectedNature?: Nature;
  natureResult!: Nature[];
  loading: boolean = false;

  ref: DynamicDialogRef | undefined;

  disableSubmit = false;
  errors: GenericErrorResponse[] = [];

  editRowIndex: number | null = null; // Variable pour garder la ligne en cours d'édition
  editingLine: any = null;
  clonedNature: { [s: string]: Nature } = {};
  index: any;
  totalRecords: number = 0;
  natureForm = this.formBuilder.group({
    id: this.formBuilder.control<number | null>(null),
    codeHecate: this.formBuilder.control<number | null>(null),
    designation: [''],
  });

  constructor(
    private natureService: ReferentialsNaturesService,
    private storeService: StoreService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private formBuilder: FormBuilder,
    private translocoService: TranslocoService,
    private filterEventService: FilterEventService,
  ) {
    effect(() => {
      const selectedContrat = this.storeService.selectedContract();
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    this.userHasReadWritePermission = this.storeService.hasAuthority('PARAMETRES_REFERENTIEL_RW');
    this.loadNature();
    this.filterSubscription = this.filterEventService.filterEvent$.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event === 'reset') {
        this.clearFilter();
      }
    });
  }

  private clearFilter() {
    this.table.clear();
  }

  private loadNature() {
    this.loading = true;
    this.natureService
      .getNatureData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.natureResult = data;
        this.totalRecords = this.natureResult.length;
        this.loading = false;
      });
  }

  ajouterNature() {
    this.ref = this.dialogService.open(AddNatureComponent, {
      ...dialogConfig,
      header: this.translocoService.translate('jgrid.nav.addtitle'),
    });
    this.ref.onClose.subscribe((response) => {
      if (response) {
        this.showSuccessMessage('La ligne a été ajouté avec succès.');
        this.loadNature();
      }
    });
  }

  modifierNature(nature: Nature, index: number) {
    this.editRowIndex = index;
    this.editingLine = nature;
    this.selectedNature = nature;
    const id = nature.id;
    this.natureForm.controls.codeHecate.patchValue(nature.codeHecate);
    this.natureForm.controls.id.patchValue(nature.id);
    this.natureForm.controls.designation.patchValue(nature.designation);
    this.clonedNature[id.toString()] = { ...nature };
  }

  validerModifsNature(nature: Nature) {
    this.natureService.editNatureData(this.natureForm.value as Nature).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  annulerModifsNature(nature: Nature, index: number) {
    this.editRowIndex = null;
    this.editingLine = null;
    const id = nature.id;
    this.natureResult
      .filter((nat) => nat.id === id)
      .map((c) => {
        if (c.id == id) {
          c = this.clonedNature[id];
        }
      });
    delete this.clonedNature[id.toString()];
  }

  private handleInlineResponse(response: InlineResponse): void {
    this.disableSubmit = false;
    if (response.success) {
      this.showSuccessMessage('La ligne a été modifiée avec succès.');
      this.loadNature();
    } else {
      this.errors = response.errors;
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('error.popup.titre.defaut'),
        detail: 'Une erreur est survenue : ' + this.errors.map((e) => e.errorMsg).join(', '),
        life: 3000,
      });
    }
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.disableSubmit = false;
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  onRowSelect(data: Nature) {
    this.selectedNature = data;
  }

  /*onFilter(event: TableFilterEvent) {
   let filterMetadata =  event.filters as ( { [s: string]: FilterMetadata | FilterMetadata[] | undefined });
    Object.entries(filterMetadata).forEach(([key, filterMetadata]) => {
      if (!!filterMetadata) {
        let metadata = Array.isArray(filterMetadata) ? filterMetadata[0] : filterMetadata;
        let data = metadata.value;
        let cleanKey = key;
        if (key.includes("value.")) {
          cleanKey = key.substring(6,key.length);
        }
        if (typeof data === 'boolean' || (!!data && data !== '')) {
          if(metadata.matchMode === 'contains') {
            of(this.natureFormArray.controls as AbstractControl[]).subscribe((formArray:  AbstractControl[])=> {
              formArray.filter((group: AbstractControl) => {
                return group.get(cleanKey)?.value.toString().toLowerCase().includes(data.toLowerCase());
              });
            })
          }
        }
      }
    });
  }*/

  showInfoMessage(infoMessage: string) {
    this.messageService.add({ severity: 'info', summary: this.translocoService.translate('info.popup.titre.defaut'), detail: infoMessage, life: 3000 });
  }

  showSuccessMessage(successMessage: string) {
    this.messageService.add({ severity: 'success', summary: this.translocoService.translate('summary.success'), detail: successMessage, life: 3000 });
  }

  showErrorMessage(errorMessage: string) {
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: errorMessage, life: 3000 });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
